import React from 'react';
import { Building2, Globe2, Users } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SectionHeader from './testimonials/SectionHeader';
import TabContent from './testimonials/TabContent';
import LoadingState from './testimonials/LoadingState';
import ErrorState from './testimonials/ErrorState';
import { useLogos } from '@/hooks/useLogos';
import { testimonialsTranslations } from '@/utils/translations/testimonials';

interface TestimonialsProps {
  language: string;
}

const Testimonials = ({ language }: TestimonialsProps) => {
  const { clientLogos, governmentLogos, partnerLogos, isLoading, error } = useLogos();
  const translations = testimonialsTranslations[language as keyof typeof testimonialsTranslations] || testimonialsTranslations.EN;

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  const tabTriggerClass =
    "flex items-center justify-center gap-2 rounded-full px-3 py-2.5 text-sm font-medium text-neutral-500 transition-all hover:text-primary data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm";

  return (
    <section className="relative overflow-hidden py-20 bg-gradient-to-b from-white via-sky-50/40 to-white">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-secondary/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={translations.eyebrow}
          title={translations.title}
          description={translations.description}
        />

        <div className="flex justify-center w-full mb-12">
          <Tabs defaultValue="clients" className="w-full max-w-2xl">
            <TabsList className="grid w-full grid-cols-3 h-auto gap-1 rounded-full bg-neutral p-1.5">
              <TabsTrigger value="clients" className={tabTriggerClass}>
                <Users className="w-4 h-4" />
                {translations.tabs.clients}
              </TabsTrigger>
              <TabsTrigger value="government" className={tabTriggerClass}>
                <Globe2 className="w-4 h-4" />
                {translations.tabs.government}
              </TabsTrigger>
              <TabsTrigger value="partners" className={tabTriggerClass}>
                <Building2 className="w-4 h-4" />
                {translations.tabs.partners}
              </TabsTrigger>
            </TabsList>

            <TabContent
              value="clients"
              logos={clientLogos}
              emptyMessage={translations.emptyStates.clients}
              viewMoreLabel={translations.viewMore}
              showLessLabel={translations.showLess}
            />
            <TabContent
              value="government"
              logos={governmentLogos}
              emptyMessage={translations.emptyStates.government}
              viewMoreLabel={translations.viewMore}
              showLessLabel={translations.showLess}
            />
            <TabContent
              value="partners"
              logos={partnerLogos}
              emptyMessage={translations.emptyStates.partners}
              viewMoreLabel={translations.viewMore}
              showLessLabel={translations.showLess}
            />
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;